import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

interface Resume {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  position: string;
  resumeUrl: string;
  submittedAt: string;
}

export default function ResumeManager() {
  const { data: resumes = [], isLoading } = useQuery({
    queryKey: ['/api/admin/resumes'],
    retry: false,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Resume Submissions</h2>
        <p className="text-slate-600 dark:text-slate-300">View and manage job applications</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(resumes) && resumes.map((resume: Resume) => (
            <motion.div
              key={resume.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          {resume.fullName}
                        </h3>
                        <Badge variant="outline">{resume.position}</Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400 mb-3">
                        <span><i className="fas fa-envelope mr-1"></i>{resume.email}</span>
                        <span><i className="fas fa-phone mr-1"></i>{resume.phone}</span>
                        <span><i className="fas fa-calendar mr-1"></i>{format(new Date(resume.submittedAt), 'MMM dd, yyyy HH:mm')}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      {(() => {
                        const url = resume.resumeUrl || '';
                        const valid = url.startsWith('/uploads/') || url.startsWith('http');
                        if (!valid) {
                          return (
                            <Button size="sm" variant="outline" disabled title="No accessible file URL">
                              <i className="fas fa-ban mr-2"></i>
                              No File
                            </Button>
                          );
                        }
                        return (
                          <Button size="sm" variant="outline" asChild>
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <i className="fas fa-download mr-2"></i>
                              View Resume
                            </a>
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          
          {(!Array.isArray(resumes) || resumes.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <i className="fas fa-file-alt text-4xl text-slate-300 dark:text-slate-600 mb-4"></i>
                <p className="text-slate-600 dark:text-slate-300">No resume submissions yet.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
